import { DeliveryTypeEnum } from '../../common/general-types/briefs.dto';
import { awarenessGoals, considerationGoals, conversionGoals } from '../../common/app-config/layout';

export interface BriefBuilderGoal {
  id: number;
  name: string;
  description: string;
  color: string;
  icon: string;
}

/** All selectable brief goals, flattened from the app's real goal config (layout.ts). */
export const BRIEF_BUILDER_GOALS: BriefBuilderGoal[] = [
  ...awarenessGoals,
  ...considerationGoals,
  ...conversionGoals,
];

export interface VideoFormatOption {
  id: number;
  name: string;
  description: string;
  color: string;
}

/** Video-format options; `id` maps 1:1 to VideoFormatEnum (0 Vertical / 1 Horizontal / 2 Square). */
export const VIDEO_FORMAT_OPTIONS: VideoFormatOption[] = [
  { id: 0, name: '9:16', description: 'Vertical', color: 'bg-green-500/10 text-green-400 rounded-l-md border-t border-l border-b border-green-400' },
  { id: 1, name: '16:9', description: 'Horizontal', color: 'bg-emerald-500/10 text-emerald-400 rounded-l-md border-t border-l border-b border-emerald-400' },
  { id: 2, name: '1:1', description: 'Square', color: 'bg-teal-500/10 text-teal-400 rounded-l-md border-t border-l border-b border-teal-400' },
];

export interface VideoDurationOption {
  id: number;
  name: string;
  image: string;
}

/** Video length options; `id` is 15 / 30 / 60 and maps to brief pricing duration fields. */
export const VIDEO_DURATION_OPTIONS: VideoDurationOption[] = [
  { id: 15, name: '0 - 15 sec', image: 'assets/icon-brief/15secs.svg' },
  { id: 30, name: '15 - 30 sec', image: 'assets/icon-brief/30secs.svg' },
  { id: 60, name: '30 - 60 sec', image: 'assets/icon-brief/60secs.svg' },
];

export interface SocialChannelOption {
  channelName: string;
  shortName: string;
  channelId: number;
  channelIcon: string;
}

/** Postable social channels (channelId maps to shoutBrief.socialPlatform / crossPlatforms). */
export const SOCIAL_CHANNELS: SocialChannelOption[] = [
  { channelName: 'TikTok Post', shortName: 'TikTok', channelId: 0, channelIcon: '../../../../assets/social-icon/tik-tok-transparent.svg' },
  { channelName: 'Instagram Reel', shortName: 'Instagram Reel', channelId: 1, channelIcon: '../../../../assets/social-icon/ig_reels_logo.png' },
  { channelName: 'Instagram Story', shortName: 'Instagram Story', channelId: 2, channelIcon: '../../../../assets/social-icon/instagram-logo.png' },
  { channelName: 'YouTube Shorts', shortName: 'YouTube', channelId: 3, channelIcon: '../../../../assets/social-icon/youtube_shorts.webp' },
  { channelName: 'Snapchat Spotlight', shortName: 'Snapchat Spotlight', channelId: 4, channelIcon: '../../../../assets/social-icon/snapchat-logo.svg' },
  { channelName: 'Snapchat Story', shortName: 'Snapchat Story', channelId: 5, channelIcon: '../../../../assets/social-icon/snapchat_story.jpg' },
];

export interface DeliveryOption {
  id: DeliveryTypeEnum;
  label: string;
  description: string;
}

export const DELIVERY_OPTIONS: DeliveryOption[] = [
  { id: DeliveryTypeEnum.none, label: 'No delivery', description: 'Creator already has access — nothing to send.' },
  { id: DeliveryTypeEnum.Discount_Code, label: 'Discount code', description: 'Send each creator a unique code to redeem the product.' },
  { id: DeliveryTypeEnum.Address_Required, label: 'Ship a product', description: 'Collect creator addresses to ship physical product.' },
];

export interface FollowerRangeOption {
  min: number;
  max: number;
  label: string;
}

/** Follower tiers (nano → mega). Selected tiers map to demographics.followerRanges as {min,max}. */
export const FOLLOWER_RANGES: FollowerRangeOption[] = [
  { min: 1000, max: 10000, label: 'Nano · 1k–10k' },
  { min: 10000, max: 50000, label: 'Micro · 10k–50k' },
  { min: 50000, max: 250000, label: 'Mid · 50k–250k' },
  { min: 250000, max: 1000000, label: 'Macro · 250k–1M' },
  { min: 1000000, max: 100000000, label: 'Mega · 1M+' },
];
